import { API, Constants } from "@nextgammon/shared";

interface ConnectionInfo {
  source: EventSource;
  refCount: number;
  lastSequence: number;
  heartbeatTimeoutId: ReturnType<typeof setTimeout> | null;
  isConnected: boolean;
}

const API_URL = process.env.BUN_PUBLIC_API_URL;
const connections = new Map<string, ConnectionInfo>();

export function getOrCreateConnection(gameId: string): ConnectionInfo {
  if (!API_URL) {
    throw new Error("BUN_PUBLIC_API_URL environment variable is not defined");
  }

  let info = connections.get(gameId);

  if (!info || info.source.readyState === EventSource.CLOSED) {
    const source = new EventSource(`${API_URL}/v1/game/events?gameId=${gameId}`);
    info = {
      source,
      refCount: 0,
      lastSequence: -1,
      heartbeatTimeoutId: null,
      isConnected: false
    };
    connections.set(gameId, info);

    // Enhanced connection state tracking
    source.onopen = () => {
      info!.isConnected = true;
      updateGlobalConnectionState();
      resetHeartbeatTimeout(info!);
    };

    source.onerror = () => {
      // Only treat as disconnected if the connection is actually closed
      if (source.readyState === EventSource.CLOSED) {
        info!.isConnected = false;
        updateGlobalConnectionState();
        clearHeartbeatTimeout(info!);
      }
      // For CONNECTING state, let reconnection logic handle it
    };

    source.onmessage = (event) => {
      // Reset heartbeat timeout on any message
      resetHeartbeatTimeout(info!);
    };

    // Listen for heartbeat events specifically
    // SSE named events require explicit addEventListener (not onmessage)
    source.addEventListener(API.Game.SSEEventType.HEARTBEAT, () => {
      resetHeartbeatTimeout(info!);
    });
  }

  return info;
}

export function releaseConnection(gameId: string): void {
  const info = connections.get(gameId);
  if (info) {
    info.refCount--;
    if (info.refCount <= 0) {
      clearHeartbeatTimeout(info);
      info.source.close();
      connections.delete(gameId);
      updateGlobalConnectionState();
    }
  }
}

// Update global connection state based on all active connections
function updateGlobalConnectionState() {
  const hasAnyConnected = Array.from(connections.values()).some((info) => info.isConnected);
  setConnectionState(hasAnyConnected);
}

// Simple connection state for UI
let connectionState: "connected" | "disconnected" = "disconnected";
const stateListeners = new Set<(state: string) => void>();

export function setConnectionState(connected: boolean) {
  const newState = connected ? "connected" : "disconnected";
  if (connectionState !== newState) {
    connectionState = newState;
    stateListeners.forEach((fn) => fn(newState));
  }
}

export function getConnectionState() {
  return connectionState;
}

export function subscribeToConnectionState(callback: (state: string) => void) {
  stateListeners.add(callback);
  return () => {
    stateListeners.delete(callback);
  };
}

// Simple heartbeat timeout management using shared constants
function resetHeartbeatTimeout(info: ConnectionInfo) {
  clearHeartbeatTimeout(info);
  info.heartbeatTimeoutId = setTimeout(() => {
    info.isConnected = false;
    updateGlobalConnectionState();
  }, Constants.CLIENT_HEARTBEAT_TIMEOUT_MS);
}

function clearHeartbeatTimeout(info: ConnectionInfo) {
  if (info.heartbeatTimeoutId) {
    clearTimeout(info.heartbeatTimeoutId);
    info.heartbeatTimeoutId = null;
  }
}

// Cleanup connections on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    connections.forEach((info, gameId) => {
      clearHeartbeatTimeout(info);
      info.source.close();
    });
    connections.clear();
  });
}
